import * as yup from "yup";

import { ReferenceType } from "../../../ui-v2/References/types";
import {
  MAX_BASE_SHIFT,
  MAX_DENOISING_STEPS,
  MAX_GUIDANCE_SCALE,
  MAX_MAX_SHIFT,
  MAX_REFERENCE_SIMILARITY,
  MAX_SEED,
  MIN_BASE_SHIFT,
  MIN_DENOISING_STEPS,
  MIN_GUIDANCE_SCALE,
  MIN_MAX_SHIFT,
  MIN_REFERENCE_SIMILARITY,
  MIN_SEED,
  QUALITY_OPTIONS,
} from "../../constants";
import { validationSchemaWithStyle } from "../../fields/Style/utils";
import { FORM_FIELDS_2D } from "./constants";
import { FormType2D } from "./types";

const referenceValidationSchema = yup.object().shape({
  image: yup
    .object()
    .shape({
      src: yup.string(),
    })
    .nullable(),
  similarity: yup
    .number()
    .typeError("Invalid value.")
    .min(
      MIN_REFERENCE_SIMILARITY,
      `Similarity must be at least ${MIN_REFERENCE_SIMILARITY}`,
    )
    .max(
      MAX_REFERENCE_SIMILARITY,
      `Similarity must be at most ${MAX_REFERENCE_SIMILARITY}`,
    ),
});

export const validationSchema = yup.object().shape({
  ...validationSchemaWithStyle,
  [FORM_FIELDS_2D.PROMPT_TEXT]: yup.string().when([FORM_FIELDS_2D.REFERENCES], {
    is: (references: ReferenceType[]) => !references || references.length === 0,
    then: (schema) => schema.required("Prompt text is required."),
  }),
  [FORM_FIELDS_2D.NEGATIVE_PROMPT_TEXT]: yup.string().nullable(),
  [FORM_FIELDS_2D.PERFORMANCE]: yup
    .string()
    .oneOf(QUALITY_OPTIONS)
    .required("Quality is required."),
  [FORM_FIELDS_2D.REFERENCES]: yup.array().of(referenceValidationSchema),
  [FORM_FIELDS_2D.SEED]: yup
    .number()
    .nullable()
    .test(
      "is-valid-seed",
      `Seed must be empty or an integer between ${MIN_SEED} and ${MAX_SEED}.`,
      (value) => value === null || (value >= MIN_SEED && value <= MAX_SEED),
    ),
  [FORM_FIELDS_2D.GUIDANCE_SCALE]: yup
    .number()
    .nullable()
    .min(
      MIN_GUIDANCE_SCALE,
      `Guidance scale must be at least ${MIN_GUIDANCE_SCALE}.`,
    )
    .max(
      MAX_GUIDANCE_SCALE,
      `Guidance scale must be at most ${MAX_GUIDANCE_SCALE}.`,
    ),
  [FORM_FIELDS_2D.BASE_SHIFT]: yup
    .number()
    .nullable()
    .min(MIN_BASE_SHIFT, `Base shift must be at least ${MIN_BASE_SHIFT}.`)
    .max(MAX_BASE_SHIFT, `Base shift must be at most ${MAX_BASE_SHIFT}.`),
  [FORM_FIELDS_2D.MAX_SHIFT]: yup
    .number()
    .nullable()
    .min(MIN_MAX_SHIFT, `Guidance scale must be at least ${MIN_MAX_SHIFT}.`)
    .max(MAX_MAX_SHIFT, `Guidance scale must be at most ${MAX_MAX_SHIFT}.`),
  [FORM_FIELDS_2D.DENOISING_STEPS]: yup
    .number()
    .typeError("Invalid value.")
    .required("Denoising steps is required")
    .min(
      MIN_DENOISING_STEPS,
      `Denoising steps must be at least ${MIN_DENOISING_STEPS}.`,
    )
    .max(
      MAX_DENOISING_STEPS,
      `Denoising steps must be at most ${MAX_DENOISING_STEPS}.`,
    ),
  [FORM_FIELDS_2D.TRANSPARENCY]: yup
    .boolean()
    .required("Transparency is required"),
  [FORM_FIELDS_2D.DIMENSIONS]: yup
    .array()
    .of(yup.number())
    .length(2)
    .required("Dimensions are required"),
  [FORM_FIELDS_2D.BATCH_SIZE]: yup.number().min(1),
}) as yup.ObjectSchema<FormType2D>;
